// pattern: Imperative Shell
// Student quiz-taking view — fetches quiz, presents questions one at a time

import type { StoredQuestion } from '../core/question-model.ts';
import { checkAnswer, selectQuestions } from '../core/quiz-engine.ts';
import { getQuiz } from '../shell/api-client.ts';
import { createElement, addListener, clearElement } from '../shell/dom.ts';
import { renderResults } from './results-view.ts';
import type { QuizResults } from './results-view.ts';
import { renderFlashcardStudy } from './flashcard-study-view.ts';

type QuizState = {
  questions: Array<StoredQuestion>;
  currentIndex: number;
  correctCount: number;
  answers: Array<number>;
  answered: boolean;
};

const LETTERS = ['A', 'B', 'C', 'D'] as const;

export function renderQuizView(container: HTMLElement, quizId: string): () => void {
  const cleanups: Array<() => void> = [];

  // Loading state
  const loadingDiv = createElement('div', { className: 'card text-center quiz-loading' });

  const spinnerEl = createElement('div', { className: 'spinner' });
  const loadingText = createElement('p', {
    textContent: 'Loading quiz...',
    className: 'text-secondary quiz-loading-text',
  });

  loadingDiv.append(spinnerEl, loadingText);
  container.appendChild(loadingDiv);

  let quizName = '';

  void (async () => {
    const result = await getQuiz(quizId);
    clearElement(container);

    if (!result.ok) {
      const errorDiv = createElement('div', { className: 'card text-center quiz-error' });

      const heading = createElement('h2', { textContent: 'Quiz Not Found' });
      const msg = createElement('p', { className: 'text-secondary quiz-error-msg' });
      msg.textContent = result.error || 'This quiz does not exist or has been removed.';

      const homeBtn = createElement('a', { textContent: 'Go Home', className: 'btn btn-primary' });
      homeBtn.setAttribute('href', '#/');

      errorDiv.append(heading, msg, homeBtn);
      container.appendChild(errorDiv);
      return;
    }

    const quiz = result.data;
    quizName = quiz.name;

    // Flashcard mode — delegate to flashcard study view
    if (quiz.mode === 'flashcard') {
      clearElement(container);
      const flashcardCleanup = renderFlashcardStudy(container, quizId, quiz);
      cleanups.push(flashcardCleanup);
      return;
    }

    const questions =
      quiz.mode === 'pool'
        ? selectQuestions(quiz.questions, 'pool', quiz.poolSize)
        : selectQuestions(quiz.questions, 'structured');

    if (questions.length === 0) {
      const emptyDiv = createElement('div', {
        className: 'card text-center empty-state quiz-empty',
      });

      const nameEl = createElement('h2');
      nameEl.textContent = quiz.name;
      const msgEl = createElement('p', { className: 'text-secondary' });
      msgEl.textContent = 'This quiz has no questions yet.';
      const linkEl = createElement('a', { textContent: 'Go Home', className: 'btn btn-primary' });
      linkEl.setAttribute('href', '#/');

      emptyDiv.append(nameEl, msgEl, linkEl);
      container.appendChild(emptyDiv);
      return;
    }

    const state: QuizState = {
      questions,
      currentIndex: 0,
      correctCount: 0,
      answers: [],
      answered: false,
    };

    // Build quiz shell
    const quizWrapper = createElement('div', { className: 'quiz-wrapper' });

    const header = createElement('div', { className: 'quiz-header' });

    const titleEl = createElement('h2', { textContent: quiz.name, className: 'quiz-title' });

    const counterEl = createElement('p', { className: 'text-secondary quiz-counter' });

    header.append(titleEl, counterEl);

    const questionArea = createElement('div', { className: 'question-area' });

    quizWrapper.append(header, questionArea);
    container.appendChild(quizWrapper);

    function renderQuestion(): void {
      // Clean up previous question listeners
      questionCleanups.forEach((fn) => fn());
      questionCleanups = [];

      const q = state.questions[state.currentIndex]!;
      state.answered = false;

      counterEl.textContent = `Question ${state.currentIndex + 1} of ${state.questions.length}`;

      clearElement(questionArea);

      const questionCard = createElement('div', { className: 'card question-card card-enter' });

      if (q.figureDataUrl) {
        const figureDiv = createElement('div', { className: 'question-figure' });
        const figureImg = createElement('img') as HTMLImageElement;
        figureImg.src = q.figureDataUrl;
        figureImg.alt = 'Question figure';
        figureDiv.appendChild(figureImg);
        questionCard.appendChild(figureDiv);
      }

      const questionText = createElement('p', { className: 'question-text' });
      questionText.textContent = q.question;

      questionCard.appendChild(questionText);

      const optionsDiv = createElement('div', { className: 'options quiz-options' });

      const optionBtns: Array<HTMLButtonElement> = [];

      q.options.forEach((opt, i) => {
        const btn = createElement('button', {
          className: `option-btn card-enter card-enter-delay-${Math.min(i + 1, 4)}`,
          textContent: `${LETTERS[i]}) ${opt}`,
        });

        const cleanup = addListener(btn, 'click', () => {
          if (state.answered) return;
          state.answered = true;
          state.answers.push(i);

          const isCorrect = checkAnswer(q, i);
          if (isCorrect) {
            state.correctCount++;
          }

          // Disable all buttons
          optionBtns.forEach((b) => {
            b.disabled = true;
          });

          // Highlight selected and correct
          btn.classList.add(isCorrect ? 'correct' : 'incorrect');
          // Add animation class for student micro-animations
          btn.classList.add(isCorrect ? 'answer-correct' : 'answer-wrong');
          if (!isCorrect) {
            optionBtns[q.correctAnswerIndex]!.classList.add('correct', 'answer-correct');
          }

          // Show feedback
          const feedbackDiv = createElement('div', {
            className: `feedback ${isCorrect ? 'feedback-section-correct' : 'feedback-section-wrong'}`,
          });

          const feedbackText = createElement('p', {
            className: `feedback-text ${isCorrect ? 'feedback-correct' : 'feedback-wrong'}`,
          });
          feedbackText.textContent = isCorrect ? '\uD83C\uDF89 Correct!' : '\uD83D\uDE2C Not quite';

          feedbackDiv.appendChild(feedbackText);

          if (q.explanation) {
            const explanation = createElement('p', { className: 'text-secondary' });
            explanation.textContent = q.explanation;
            feedbackDiv.appendChild(explanation);
          }

          const isLast = state.currentIndex === state.questions.length - 1;

          const nextBtn = createElement('button', {
            className: 'btn btn-primary quiz-next-btn',
            textContent: isLast ? 'See Results' : 'Next Question',
          });

          const nextCleanup = addListener(nextBtn, 'click', () => {
            if (isLast) {
              showResults();
            } else {
              state.currentIndex++;
              renderQuestion();
            }
          });
          questionCleanups.push(nextCleanup);

          feedbackDiv.appendChild(nextBtn);
          questionCard.appendChild(feedbackDiv);
        });
        questionCleanups.push(cleanup);

        optionBtns.push(btn);
        optionsDiv.appendChild(btn);
      });

      questionCard.appendChild(optionsDiv);
      questionArea.appendChild(questionCard);
    }

    function showResults(): void {
      // Clean up question listeners
      questionCleanups.forEach((fn) => fn());
      questionCleanups = [];

      clearElement(container);

      const results: QuizResults = {
        quizId,
        quizName,
        questions: state.questions,
        answers: state.answers,
        correctCount: state.correctCount,
        totalQuestions: state.questions.length,
      };

      const resultsCleanup = renderResults(container, results);
      cleanups.push(resultsCleanup);
    }

    let questionCleanups: Array<() => void> = [];
    cleanups.push(() => questionCleanups.forEach((fn) => fn()));

    renderQuestion();
  })();

  return () => {
    cleanups.forEach((fn) => fn());
  };
}
